import { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
  Modal as RNModal,
  ScrollView,
} from 'react-native';
import {
  MaterialCommunityIcons,
  Octicons,
  MaterialIcons,
  Ionicons,
  AntDesign,
} from '@expo/vector-icons';
import Feather from '@expo/vector-icons/Feather';
import { ContentIcon, Content } from './style';
import THEME from '../../config/theme';
import { FooterText, SmallText, Title } from '../../config/theme/globalStyles';
import {
  getDoc,
  setDoc,
  doc,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import useViewport from '../../hooks/useViewport';
import CopyContentModal from '../Modals/CopyContentModal';
import { ADMIN_FONT } from '../../config/theme/adminColors';

// `system-ui` não carrega peso no nome da família (diferente de `text_Medium`),
// então o destaque dos rótulos de ação vem do fontWeight.
const adminActionLabel = { fontFamily: ADMIN_FONT, fontWeight: '600' };

const ListHeader = ({
  cardId,
  categoryId,
  videoId,
  video,
  onEditCardInformationClick,
  onEditOrderClick,
  onDeleteCardClick,
  user,
  login,
  adminOptions,
}) => {
  const { customColor, customFont, customInfo } = useCustomTheme();
  const { width } = useViewport();
  const Container = width < 1080 ? View : ScrollView;

  const [showModuleModal, setShowModuleModal] = useState(false);

  //adminRole "supportOnly" only allows access to notiications, community and comments
  const hasAdminRole = !user?.adminRole

  const handleCopyModulePress = () => {
    setShowModuleModal(true);
  };

  const [updatedPdf, setUpdatedPdf] = useState([]);
  useEffect(() => {
    if (video?.pdf !== undefined) {
      if (Array.isArray(video?.pdf)) {
        setUpdatedPdf(video?.pdf);
      } else if (typeof video?.pdf === 'string' && video?.pdf.trim() === '') {
        setUpdatedPdf([]);
      } else if (typeof video?.pdf === 'string') {
        setUpdatedPdf([{ url: video?.pdf, name: '', title: video.pdf?.title || 'PDF' }]);
      } else {
        setUpdatedPdf([video.pdf]);
      }
    }
  }, [video?.pdf]);

  const [watched, setWatched] = useState(false);
  const [like, setLike] = useState(false);
  const [loadingWatchedUpdate, setLoadingWatchedUpdate] = useState(true);
  const [loadingLikeUpdate, setLoadingLikeUpdate] = useState(true);
  const [activeProgress, setActiveProgress] = useState(false);

  const intervalIdRef = useRef(null);
  const scriptTagRef = useRef(null);

  const videoUrl =
    video?.videoInfo?.storage == 'panda' ||
    video?.pandaVideoMetadata?.video_player ||
    (video?.link && !/(youtube|youtu\.be|firebasestorage\.googleapis|vimeo)/.test(video.link));

  useEffect(() => {
    if (!user?.isAdmin && !scriptTagRef.current && videoUrl) {
      const scriptTag = document.createElement('script');
      scriptTag.src = 'https://player.pandavideo.com.br/api.v2.js';
      scriptTag.async = true;
      document.body.appendChild(scriptTag);
      scriptTagRef.current = scriptTag;

      scriptTag.onload = () => {
        window.pandascripttag = window.pandascripttag || [];
        window.pandascripttag.push(() => {
          const player = new PandaPlayer('panda-1', {
            onReady: () => {
              const intervalId = setInterval(() => {
                const progress =
                  (player.getCurrentTime() * 100) / player.getDuration();

                if (progress >= 95 && !watched) {
                  setWatched(true);
                  setActiveProgress(true);
                  handleUpdateWatched();
                }
              }, 500);

              // Store intervalId in the intervalIdRef
              intervalIdRef.current = intervalId;
            },
          });
        });
      };
    }

    return () => {
      const intervalId = intervalIdRef.current;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user?.isAdmin, watched]);
  const handleUpdateLike = useCallback(async () => {
    if (loadingLikeUpdate) {
      return;
    }
    setLoadingLikeUpdate(true);

    let date = new Date();

    try {
      const contentsDocRef = doc(
        firestore,
        'users',
        user?.id,
        'contents',
        videoId
      );
      await setDoc(
        contentsDocRef,
        {
          cardId,
          categoryId,
          markedAsFavoriteAt: date,
          isFavorite: !like,
        },
        { merge: true }
      );
      setLike(!like);
    } catch (error) {
      console.error('Error adding document isFavorite: ', error);
    }
    setLoadingLikeUpdate(false);
  }, [loadingLikeUpdate, user?.id, videoId, like]);

  const handleUpdateWatched = useCallback(async () => {
    if (loadingWatchedUpdate && watched) {
      return;
    } else if (activeProgress) {
      return;
    }
    setLoadingWatchedUpdate(true);

    let date = new Date();

    try {
      const contentsDocRef = doc(
        firestore,
        'users',
        user?.id,
        'contents',
        videoId
      );
      await setDoc(
        contentsDocRef,
        {
          cardId,
          categoryId,
          markedAsWatchedAt: date,
          isWatched: true,
        },
        { merge: true }
      );
      setWatched(true);
    } catch (error) {
      console.error('Error adding document isWatched: ', error);
    }
    setLoadingWatchedUpdate(false);
  }, [loadingWatchedUpdate, user?.id, videoId, watched]);

  const loadDetail = useCallback(async () => {
    try {
      const watchedDocRef = doc(
        firestore,
        'users',
        user?.id,
        'contents',
        videoId
      );
      const docSnap = await getDoc(watchedDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setWatched(data.isWatched);
        setLike(data.isFavorite);
      }
      setLoadingLikeUpdate(false);
      setLoadingWatchedUpdate(false);
    } catch (error) {
      console.log(error);
    }
  }, [user?.id, videoId]);

  useEffect(() => {
    loadDetail();
  }, []);

  const handlePress = (link) => {
    if (login) {
      window.open(link, '_blank');
    }
  };

  const Pdf = ({ pdfs }) => {
    const [modalVisible, setModalVisible] = useState(false);

    const openModal = () => {
      setModalVisible(true);
    };

    const closeModal = () => {
      setModalVisible(false);
    };

    const namePdf = (item) => {
      // `slice(24)` corta o prefixo com que o arquivo é gravado no Storage.
      // Com nome curto o corte devolvia string vazia e a linha ficava em
      // branco — daí os fallbacks.
      const stripped = item?.name?.slice(24);

      if (!item?.title || item?.name === item?.title) {
        return stripped || item?.name || 'PDF';
      }
      return item.title;
    };

    return (
      <ContentIcon onPress={openModal} padding="0rem 0.5rem">
        <Ionicons name="document-text-outline" size={24}
          color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
        <FooterText margin="0.5rem 0rem" fontSize={9} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
          Arquivo
        </FooterText>
        <RNModal
          visible={modalVisible}
          transparent
          animationType="fade"
          onRequestClose={closeModal}
        >
          {/* Centralizado na tela. Antes era `flex-start` + `paddingTop: 20%`,
              que no celular jogava a caixa para o topo. O padding do fundo
              garante que a caixa nunca encoste nas bordas. */}
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: 16,
            }}
          >
            <View
              style={{
                // `60%` da tela dava ~200px num celular: o título do PDF não
                // cabia em lugar nenhum. Aqui a caixa ocupa a largura
                // disponível e para de crescer no desktop.
                width: '100%',
                maxWidth: 420,
                maxHeight: '80%',
                backgroundColor: 'white',
                padding: 16,
                borderRadius: 12,
              }}
            >
              {/* Linha do topo: sem `flex-wrap` (era o `Content`, que embrulhava
                  e mandava o X para baixo do texto) e com o X ancorado no canto
                  superior direito. */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <SmallText
                  textAlign="left"
                  style={{
                    flex: 1,
                    fontFamily:
                      customFont && customFont.fontFamilyText
                        ? customFont.fontFamilyText
                        : THEME.FONTFAMILY.REGULAR,
                  }}
                  color="black"
                >
                  Selecione um PDF:
                </SmallText>
                <TouchableOpacity
                  onPress={closeModal}
                  hitSlop={12}
                  accessibilityRole="button"
                  accessibilityLabel="Fechar"
                  style={{ marginLeft: 12 }}
                >
                  <AntDesign name="closecircleo" size={20} color="black" />
                </TouchableOpacity>
              </View>
              <FlatList
                data={pdfs}
                keyExtractor={(item) => item?.url}
                renderItem={({ item }) => (
                  <View
                    style={{
                      paddingVertical: 10,
                      borderBottomWidth: 1,
                      borderBottomColor: '#ccc',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    {/* `flex: 1`: sem isto o item não encolhe (padrão do RN), o
                        título estourava a caixa e era cortado pela borda em vez
                        de quebrar em duas linhas. */}
                    <TouchableOpacity
                      onPress={() => {
                        window.open(item.url, '_blank');
                      }}
                      style={{ flex: 1 }}
                    >
                      <SmallText
                        textAlign="left"
                        style={{
                          fontFamily:
                            customFont && customFont.fontFamilyText
                              ? customFont.fontFamilyText
                              : THEME.FONTFAMILY.REGULAR,
                        }}
                        color="#2e2e2e"
                      >
                        {namePdf(item)}
                      </SmallText>
                    </TouchableOpacity>
                  </View>
                )}
              />
            </View>
          </View>
        </RNModal>
      </ContentIcon>
    );
  };

  const Link = () => (
    <ContentIcon padding="0rem 0.5rem">
      <TouchableOpacity onPress={() => {
        const url = video?.url;
        if (url.includes("pandavideo")) {
          const safeUrl = `${customInfo?.webUrl}/Watch?categoryId=${categoryId}&cardId=${cardId}&selectedVideo=${videoId}`;
          handlePress(safeUrl)
        } else {
          handlePress(video?.url)
        }
      }}
      >
        <Octicons name="link-external" size={24}
          color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
        <FooterText margin="0.5rem 0rem" fontSize={9} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
          Link
        </FooterText>
      </TouchableOpacity>
    </ContentIcon>
  );

  const Favorite = () => (
    <ContentIcon disabled={loadingLikeUpdate} onPress={handleUpdateLike} padding="0rem 0.5rem">
      {loadingLikeUpdate ? (
        <ActivityIndicator size={24}
          color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      ) : (
        <Ionicons name={like ? 'ios-heart-sharp' : 'ios-heart-outline'} size={24}
          color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      )}
      <FooterText margin="0.5rem 0rem" fontSize={9} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
        {like ? 'Favoritado' : 'Favoritar'}
      </FooterText>
    </ContentIcon>
  );

  const Watched = () => (
    <ContentIcon disabled={loadingWatchedUpdate || watched} onPress={handleUpdateWatched} padding="0rem 0.5rem">
      {loadingWatchedUpdate ? (
        <ActivityIndicator size={24}
          color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      ) : (
        <Ionicons name={watched ? 'md-checkmark-circle' : 'md-checkmark-circle-outline'} size={24}
          color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      )}
      <FooterText
        margin="0.5rem 0rem"
        fontSize={9}
        style={{
          fontFamily:
            customFont && customFont.fontFamilyText
              ? customFont.fontFamilyText
              : THEME.FONTFAMILY.LIGHT,
        }}
        color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
        {watched ? 'Concluído' : 'Concluir'}
      </FooterText>
    </ContentIcon>
  );

  // Botões de ação — visíveis SÓ para o admin, então o rótulo usa a tipografia
  // do painel (ADMIN_FONT). A cor continua vindo do tema do cliente porque a
  // barra fica sobre o fundo temático da tela de conteúdo.
  const Edit = () => (
    <ContentIcon onPress={onEditCardInformationClick}>
      <MaterialCommunityIcons name="playlist-edit" size={26}
        color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      <FooterText fontSize={9} style={adminActionLabel} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
        Editar
      </FooterText>
    </ContentIcon>
  );

  const CopyModuleButton = () => (
    <ContentIcon onPress={handleCopyModulePress}>
      <Feather name="copy" size={24}
        color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      <FooterText fontSize={9} style={adminActionLabel} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
        Copiar Módulo
      </FooterText>
    </ContentIcon>
  );

  const EditOrderButton = () => (
    <ContentIcon onPress={onEditOrderClick}>
      <Octicons name="list-ordered" size={24}
        color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      <FooterText fontSize={9} style={adminActionLabel} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
        Ordem
      </FooterText>
    </ContentIcon>
  );

  const DeleteCard = () => (
    <ContentIcon onPress={onDeleteCardClick}>
      <MaterialIcons name="delete-outline" size={26}
        color={customColor.text?.app || THEME.COLORS.TEXT_MAIN} />
      <FooterText fontSize={9} style={adminActionLabel} color={customColor.text?.app || THEME.COLORS.TEXT_MAIN}>
        Excluir
      </FooterText>
    </ContentIcon>
  );

  return (
    <Container>

      {user?.isAdmin && adminOptions && hasAdminRole && (
        <Content justifyContent="space-around" margin="1rem 0">
          <Edit />
          <CopyModuleButton />
          {onEditOrderClick && <EditOrderButton />}
          <DeleteCard />
        </Content>
      )}


      <Title
        fontSize={customFont?.fontLX || THEME?.FONTSIZE?.BIG}
        style={{ fontFamily: customFont?.fontFamilyTitle || THEME?.FONTFAMILY?.BOLD, marginBottom: '1rem' }}
        color={customColor?.text?.app || THEME?.COLORS?.TITLE_MAIN}
        textAlign="left"
      >
        {video.title}
      </Title>

      <Content>
        {updatedPdf.length > 0 && <Pdf pdfs={updatedPdf} />}
        {video.url && <Link />}
        {!user?.isAdmin && user?.id && (
          <>
            <Favorite />
            <Watched />
          </>
        )}
      </Content>


      {video?.description && (
        <SmallText
          fontSize={customFont?.fontSM || THEME?.FONTSIZE?.SMALL}
          style={{ fontFamily: customFont?.fontFamilyText || THEME?.FONTFAMILY?.REGULAR }}
          color={customColor?.text?.app || THEME?.COLORS.TEXT_MAIN}
          margin="0 0 1rem 0"
          textAlign="left"
        >
          {Array.isArray(video.description)
            ? video.description.join('\n')
            : video.description}
        </SmallText>
      )}

      <CopyContentModal
        visible={showModuleModal}
        onDismiss={() => setShowModuleModal(false)}
        mode="module"
        sourceCategoryId={categoryId}
        sourceCardId={cardId}
        onSuccess={(destCat) => {
          setShowModuleModal(false);
        }}
      />
    </Container>
  );
};

export default ListHeader;